import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { addUpload } from "./UploadsSlice";
import appConfig from "../../appConfig";
import { useMsal } from "@azure/msal-react";
import { getToken } from "../../services/apiService";
import {
  buildTemplateFile,
  downloadFile,
  DEFAULT_ENVIRONMENTS,
  PREVIOUS_RUN,
  ORACLE_PLANTS,
  ECO_DETAILS_OPTIONS,
  isSectionOnly,
  environmentHint,
  canCompareWith,
  defaultCompareWith,
  rowNeedsCodestring,
  rowUsesAgile,
  rowUsesOracle,
  rowComparesWithOrder,
  sourceGroups,
  compareGroups,
  orderPairHint,
} from "./template";
import { AGILE_SIGN_IN_REQUIRED, AgileSessionStatus, AgileSignInModal, useAgileSession } from "./AgileSignIn";
import PreviousRunSelect from "./PreviousRunSelect";
import AgileRevisionSelect from "./AgileRevisionSelect";
import CodeStringBreakdown from "./CodeStringBreakdown";
import { askNotificationPermission } from "./useFinishedNotifications";
import FieldHelp from "../help/FieldHelp";
import "./FileUpload.css";

const MAX_CODESTRING_LENGTH = 570;
const MAX_CONFIGURATION_ID_LENGTH = 100;
const MAX_ORDER_LENGTH = 32;
const UNSAFE_ESOI_CHARACTERS = /[+&#%?/\\]/;
const MAX_TITLE_LENGTH = 128;
const MAX_SECTION_LENGTH = 64;

// Stable key of a row, so removing or duplicating one keeps the state of the others
let nextRowKey = 1;

const buildEmptyManualRow = () => ({
  key: nextRowKey++,
  configurationId: "",
  formatId: "",
  codeString: "",
  environmentA: "Dev",
  environmentB: "Prd",
  previousRunId: "",
  sectionOnly: false,
  sectionToProcess: "",
  plant: "",
  // Infor vs Agile / Oracle: ESOI or work order of the order
  order: "",
  // Agile version to read, as Agile lists it; empty = the one Agile opens
  agileRevision: "",
  includeEcoDetails: ECO_DETAILS_OPTIONS[0],
  enablePostConfigQueue: false,
});

const SECTION_ONLY_SUFFIX = "_sectonly";

// A section-only run is an SOI ending in _sectonly: the switch adds it, and one typed by hand counts too
const isSectionRun = (row) => row.sectionOnly || isSectionOnly(row.configurationId);

const effectiveConfigurationId = (row) => {
  const id = row.configurationId.trim();
  return row.sectionOnly && !isSectionOnly(id) ? `${id}${SECTION_ONLY_SUFFIX}` : id;
};

const EnvironmentHint = ({ text }) =>
  text ? (
    <div className="upload-hint">
      <span className="upload-hint__icon" aria-hidden="true">i</span>
      <span>{text}</span>
    </div>
  ) : null;

// The breakdown reads the code items of the format from RDM
const canBreakDown = (row) => /^\d+$/.test(String(row.formatId)) && !!row.codeString.trim();

const isRowEmpty = (row) =>
  !row.configurationId.trim() && !row.formatId && !row.codeString.trim();

// Errors by field, shown under the field that has the problem
const validateRow = (row, environments) => {
  const errors = {};
  const needsCodestring = rowNeedsCodestring(row, environments);
  if (!row.configurationId.trim()) {
    errors.configurationId = needsCodestring ? "Enter the ESOI" : "Enter the ESOI or the Work Order";
  } else if (needsCodestring && effectiveConfigurationId(row).length > MAX_CONFIGURATION_ID_LENGTH) {
    errors.configurationId = `The ESOI plus ${SECTION_ONLY_SUFFIX} can't be longer than ${MAX_CONFIGURATION_ID_LENGTH} characters`;
  } else if (needsCodestring && UNSAFE_ESOI_CHARACTERS.test(row.configurationId)) {
    errors.configurationId = "The ESOI can't have + & # % ? / or \\, the simulator can't open it. Use letters, numbers, spaces, - or _";
  } else if (!needsCodestring && row.configurationId.trim().length > MAX_ORDER_LENGTH) {
    errors.configurationId = `The ESOI or Work Order can't be longer than ${MAX_ORDER_LENGTH} characters`;
  }
  if (needsCodestring) {
    if (isSectionRun(row) && !row.sectionToProcess.trim()) errors.sectionToProcess = "Enter the section to run";
    if (!row.formatId) errors.formatId = "Enter the Format Number";
    else if (!/^\d+$/.test(String(row.formatId))) errors.formatId = "Must be a whole number";
    if (!row.codeString.trim()) errors.codeString = "Paste the CodeString";
  }
  if (!canCompareWith(row.environmentA, row.environmentB, environments)) {
    errors.environmentB = "Choose a Compare With that can be compared with this Environment";
  }
  if (row.environmentB === PREVIOUS_RUN && !row.previousRunId) errors.previousRunId = "Choose the previous run to compare against";
  if (rowUsesOracle(row, environments) && !row.plant) errors.plant = "Choose the plant";
  if (rowComparesWithOrder(row, environments) && !row.order.trim()) errors.order = "Enter the ESOI or Work Order to compare with";
  return errors;
};

// Order of the fields on screen, to focus the first one with an error
const FIELD_ORDER = ["configurationId", "formatId", "environmentB", "codeString", "order", "plant", "previousRunId", "sectionToProcess"];

const fieldId = (field, index) => `${field}-${index}`;

// id, invalid state and error link of a field
const fieldProps = (field, index, errors, className) => ({
  id: fieldId(field, index),
  className: `${className}${errors?.[field] ? " is-invalid" : ""}`,
  "aria-invalid": errors?.[field] ? true : undefined,
  "aria-describedby": errors?.[field] ? `${fieldId(field, index)}-error` : undefined,
});

const FieldError = ({ field, index, errors }) =>
  errors?.[field] ? (
    <div id={`${fieldId(field, index)}-error`} className="invalid-feedback d-block">
      {errors[field]}
    </div>
  ) : null;

// Grows with the text, from two lines
const AutoGrowTextarea = (props) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight + 2}px`;
  }, [props.value]);
  return <textarea ref={ref} rows={2} {...props} />;
};

export default function FileUpload() {
  const { instance, accounts } = useMsal();
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  // Optional; without it the API uses the file name or "Test set #Id"
  const [title, setTitle] = useState("");
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  // { tone: "danger" | "success", text, rejectedRows }
  const [message, setMessage] = useState(null);
  const [rowErrors, setRowErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [manualRows, setManualRows] = useState([buildEmptyManualRow()]);
  // Agile, Oracle and Cloud only when the API offers them
  const [environments, setEnvironments] = useState(() =>
    DEFAULT_ENVIRONMENTS.filter((env) => env.kind !== "Agile" && env.kind !== "Oracle" && env.kind !== "Cloud")
  );
  const agile = useAgileSession();
  const [showAgileSignIn, setShowAgileSignIn] = useState(false);
  // What to do once the Agile sign-in succeeds: send the upload, load the versions...
  const [afterAgileSignIn, setAfterAgileSignIn] = useState(null);
  // Row whose codestring is open in the breakdown
  const [breakdownRow, setBreakdownRow] = useState(null);
  const breakdown = breakdownRow !== null ? manualRows[breakdownRow] : null;

  // The API owns the list of environments; keep the defaults if it can't be loaded
  useEffect(() => {
    let cancelled = false;
    const loadEnvironments = async () => {
      try {
        const accessToken = await getToken(instance, accounts);
        if (!accessToken) return;
        const response = await axios.get(`${appConfig.apiUrl}environments`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!cancelled && response.data?.status === "OK" && response.data.data?.length) {
          setEnvironments(response.data.data);
        }
      } catch (err) {
        // Defaults are enough to create uploads
      }
    };
    loadEnvironments();
    return () => {
      cancelled = true;
    };
  }, [instance, accounts]);

  const showError = (text) => setMessage({ tone: "danger", text });

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const chooseFile = (selectedFile) => {
    if (!selectedFile) return;
    // Browsers don't always report a MIME type for .xlsx, so check the extension. The backend can't read .xls
    if (selectedFile.name.toLowerCase().endsWith(".xlsx")) {
      setFile(selectedFile);
      setMessage(null);
      setRowErrors({});
    } else {
      clearFile();
      showError("Choose an .xlsx file based on the BOM Compare template.");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    chooseFile(e.dataTransfer.files?.[0]);
  };

  const handleManualRowChange = (index, field, value) => {
    setManualRows((prevRows) =>
      prevRows.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        const next = { ...row, [field]: value };
        // Keep Compare With valid when the Environment changes
        if (field === "environmentA" && !canCompareWith(value, next.environmentB, environments)) {
          next.environmentB = defaultCompareWith(value, environments);
        }
        return next;
      })
    );
    setRowErrors((prev) => (prev[index]?.[field] ? { ...prev, [index]: { ...prev[index], [field]: undefined } } : prev));
  };

  const addManualRow = () => {
    setManualRows((prevRows) => [...prevRows, buildEmptyManualRow()]);
  };

  // Same inputs in a new row, e.g. to run the codestring in another environment
  const duplicateManualRow = (index) => {
    setManualRows((prevRows) => [...prevRows.slice(0, index + 1), { ...prevRows[index], key: nextRowKey++ }, ...prevRows.slice(index + 1)]);
    setRowErrors({});
  };

  const removeManualRow = (index) => {
    setManualRows((prevRows) => (prevRows.length === 1 ? prevRows : prevRows.filter((_, rowIndex) => rowIndex !== index)));
    setRowErrors({});
  };

  // The template is built by the API so it always matches what the upload validation expects
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const accessToken = await getToken(instance, accounts);
      if (!accessToken) {
        showError("There was an authentication error. Please sign in again.");
        return;
      }
      const response = await axios.get(`${appConfig.apiUrl}uploads/template`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        responseType: "blob",
      });
      downloadFile(new File([response.data], "BOM-Compare-Template.xlsx"));
    } catch (err) {
      showError("The template could not be downloaded. Please try again.");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const buildManualFile = () => {
    const errors = {};
    const rowsToSend = [];
    manualRows.forEach((row, index) => {
      if (isRowEmpty(row)) return;
      const rowValidation = validateRow(row, environments);
      if (Object.keys(rowValidation).length) {
        errors[index] = rowValidation;
      } else {
        rowsToSend.push({
          ...row,
          configurationId: effectiveConfigurationId(row),
          codeString: row.codeString.trim(),
          sectionToProcess: row.sectionToProcess.trim(),
          order: row.order.trim(),
        });
      }
    });

    setRowErrors(errors);
    const withErrors = Object.keys(errors).map(Number);
    if (withErrors.length) {
      showError(withErrors.length === 1 ? `Fix row ${withErrors[0] + 1} before creating the test set.` : `Fix rows ${withErrors.map((i) => i + 1).join(", ")} before creating the test set.`);
      // Take the person to the first field with a problem
      const first = withErrors[0];
      const field = FIELD_ORDER.find((name) => errors[first][name]);
      setTimeout(() => document.getElementById(fieldId(field, first))?.focus(), 0);
      return null;
    }
    if (!rowsToSend.length) {
      showError("Fill at least one row or import the Excel template.");
      setTimeout(() => document.getElementById(fieldId("configurationId", 0))?.focus(), 0);
      return null;
    }
    return buildTemplateFile(rowsToSend, `manual-input-${Date.now()}.xlsx`);
  };

  // Opens the Agile sign-in and runs then once signed in
  const requestAgileSignIn = (then) => {
    // The session may have ended in Agile: show the status as it is now
    agile.refresh();
    setAfterAgileSignIn(() => then || null);
    setShowAgileSignIn(true);
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    setMessage(null);
    askNotificationPermission();

    const fileToUpload = file || buildManualFile();
    if (!fileToUpload) return;

    // Agile rows need the person's Agile session; ask for it first, then send. The API also checks with Agile
    // that the session still works and asks for the sign-in again if it ended.
    const manualUsesAgile = !file && manualRows.some((row) => !isRowEmpty(row) && rowUsesAgile(row, environments));
    if (manualUsesAgile && !agile.session.signedIn) {
      requestAgileSignIn(() => sendUpload(fileToUpload, true));
      return;
    }
    await sendUpload(fileToUpload, !file);
  };

  const handleAgileSignedIn = async () => {
    setShowAgileSignIn(false);
    const then = afterAgileSignIn;
    setAfterAgileSignIn(null);
    if (then) await then();
  };

  const handleAgileChangeCredentials = async () => {
    await agile.signOut();
    setShowAgileSignIn(true);
  };

  const sendUpload = async (fileToUpload, isManual) => {
    const accessToken = await getToken(instance, accounts);
    if (!accessToken) {
      showError("There was an authentication error. Please sign in again.");
      return;
    }

    const formData = new FormData();
    formData.append("file", fileToUpload);
    if (title.trim()) formData.append("title", title.trim());
    setLoading(true);

    try {
      const response = await axios.post(`${appConfig.apiUrl}uploads`, formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      });

      // The API answers 200 with status "Error" for validation problems
      const result = response.data;
      if (result?.status !== "OK" || !result.data) {
        // A template with Agile rows, or a session Agile already ended: sign in and send the same file again
        if (result?.message === AGILE_SIGN_IN_REQUIRED) {
          requestAgileSignIn(() => sendUpload(fileToUpload, isManual));
          return;
        }
        showError(result?.message || "There was an error creating the test set.");
        return;
      }

      const upload = result.data;
      dispatch(addUpload(upload));
      setMessage({ tone: "success", text: `Test set created: ${upload.total} input(s) queued.`, rejectedRows: upload.rejectedRows || [] });
      setTitle("");
      clearFile();
      if (isManual) {
        setManualRows([buildEmptyManualRow()]);
      }
    } catch (err) {
      showError(err.response?.data?.message || "There was an error creating the test set.");
    } finally {
      setLoading(false);
    }
  };

  const agileAvailable = environments.some((env) => env.kind === "Agile");
  const showAgileStatus = agileAvailable && (file || manualRows.some((row) => rowUsesAgile(row, environments)));
  const filledRows = manualRows.filter((row) => !isRowEmpty(row)).length;

  return (
    <div className="upload-panel card border-0 shadow-sm mb-4 text-start">
      <form onSubmit={handleFileUpload} noValidate>
        <div className="card-body p-4 pb-3">
          <h3 className="upload-panel__title mb-1">New test set</h3>
          <p className="upload-panel__subtitle mb-3">
            Simulate codestrings in Infor on-prem and compare them with another environment, a previous run or
            the order in Agile or Oracle. Agile can also be compared with Oracle.
          </p>

          <div className="upload-title mb-3">
            <label className="form-label upload-label" htmlFor="upload-title">
              Name <span className="text-muted fw-normal">(optional)</span>
            </label>
            <input
              id="upload-title"
              type="text"
              className="form-control"
              maxLength={MAX_TITLE_LENGTH}
              placeholder={file ? file.name.replace(/\.xlsx$/i, "") : "e.g. CARA release checks"}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              aria-describedby="upload-title-hint"
            />
            <div id="upload-title-hint" className="form-text">
              Without a name it uses the Excel file name, or Test set #number. You can rename it later.
            </div>
          </div>

          <div
            className={`upload-import${dragging ? " upload-import--dragging" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            data-tour="import"
          >
            <input
              ref={fileInputRef}
              id="upload-file"
              type="file"
              accept=".xlsx"
              className="visually-hidden"
              onChange={(e) => chooseFile(e.target.files[0])}
            />
            <span className="upload-import__label">Import from Excel</span>
            {file ? (
              <>
                <span className="upload-file-pill" title={file.name}>{file.name}</span>
                <button type="button" className="btn btn-sm btn-link px-1" onClick={clearFile}>
                  Remove file
                </button>
              </>
            ) : (
              <label htmlFor="upload-file" className="btn btn-sm btn-outline-primary mb-0">
                Choose .xlsx file
              </label>
            )}
            <button
              type="button"
              className="btn btn-sm btn-link px-1"
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate}
            >
              {downloadingTemplate ? "Downloading..." : "Download template"}
            </button>
            {!file && <span className="upload-import__drop d-none d-md-inline">or drop the file here</span>}
          </div>

          {file ? (
            <div className="upload-file-note">
              The inputs are read from <b>{file.name}</b>. Remove the file to fill the rows here instead.
            </div>
          ) : (
            <>
              <div className="upload-rows mt-3">
                {manualRows.map((row, index) => {
                  const errors = rowErrors[index];
                  const needsCodestring = rowNeedsCodestring(row, environments);
                  // Simulating in Infor needs the codestring; reading an order needs only its ESOI or work order
                  const simulates = needsCodestring;
                  return (
                    <fieldset
                      key={row.key}
                      className={`upload-row-card${errors ? " upload-row-card--invalid" : ""}`}
                      aria-label={`Row ${index + 1}`}
                    >
                      <div className="upload-row-card__header">
                        <span className="upload-row-card__index">Row {index + 1}</span>
                        <div className="upload-row-card__actions">
                          <button type="button" className="btn btn-sm btn-link" onClick={() => duplicateManualRow(index)}>
                            Duplicate
                          </button>
                          {manualRows.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-danger"
                              onClick={() => removeManualRow(index)}
                              aria-label={`Remove row ${index + 1}`}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="row g-3 mt-0">
                        <div className="col-md-6 col-lg-3" data-tour={index === 0 ? "bom-from" : undefined}>
                          <label className="form-label upload-label" htmlFor={fieldId("environmentA", index)}>BOM from</label>
                          <FieldHelp label="BOM from" section="create">
                            Where the BOM comes from. On-Premise simulates the codestring in Infor. Agile, Oracle BOM
                            and Oracle BOM Viewer read a real order, no codestring needed.
                          </FieldHelp>
                          <select
                            id={fieldId("environmentA", index)}
                            className="form-select"
                            value={row.environmentA}
                            onChange={(e) => handleManualRowChange(index, "environmentA", e.target.value)}
                          >
                            {sourceGroups(environments).map((group) => (
                              <optgroup key={group.label} label={group.label}>
                                {group.options.map((env) => (
                                  <option key={env.key} value={env.key}>{env.label}</option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                          {simulates && <EnvironmentHint text={environmentHint(row.environmentA, environments)} />}
                        </div>
                        <div className="col-md-6 col-lg-3" data-tour={index === 0 ? "compare-with" : undefined}>
                          <label className="form-label upload-label" htmlFor={fieldId("environmentB", index)}>Compare with</label>
                          <FieldHelp label="Compare with" section="compare-with">
                            What the BOM is compared with: another environment, the Production Database, one of your previous
                            runs or an order in Agile or Oracle. Only get the BOM gives you the Raw BOM without comparing. The
                            list only shows what can be compared.
                          </FieldHelp>
                          <select
                            {...fieldProps("environmentB", index, errors, "form-select")}
                            value={row.environmentB}
                            onChange={(e) => handleManualRowChange(index, "environmentB", e.target.value)}
                          >
                            {compareGroups(row.environmentA, environments).map((group) => (
                              <optgroup key={group.label} label={group.label}>
                                {group.options.map((option) => (
                                  <option key={option.key} value={option.key}>{option.label}</option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                          <FieldError field="environmentB" index={index} errors={errors} />
                          <EnvironmentHint
                            text={simulates ? environmentHint(row.environmentB, environments) : orderPairHint(row.environmentA, row.environmentB, environments)}
                          />
                        </div>
                        <div className={simulates ? "col-md-6 col-lg-3" : "col-md-6 col-lg-4"}>
                          <label className="form-label upload-label" htmlFor={fieldId("configurationId", index)}>
                            {simulates ? "ESOI *" : "ESOI or Work Order *"}
                          </label>
                          <input
                            type="text"
                            {...fieldProps("configurationId", index, errors, "form-control")}
                            placeholder={simulates ? "e.g. E123456" : "e.g. COST2933MCS1 or P9034160"}
                            maxLength={simulates ? MAX_CONFIGURATION_ID_LENGTH : MAX_ORDER_LENGTH}
                            value={row.configurationId}
                            onChange={(e) => handleManualRowChange(index, "configurationId", e.target.value)}
                          />
                          <FieldError field="configurationId" index={index} errors={errors} />
                          {!simulates && (
                            <EnvironmentHint text="Enter one, the other is found in Oracle. With an ESOI, the Viewer uses its latest work order." />
                          )}
                        </div>
                        {simulates && (
                          <>
                            <div className="col-md-6 col-lg-3">
                              <label className="form-label upload-label" htmlFor={fieldId("formatId", index)}>Format Number *</label>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                {...fieldProps("formatId", index, errors, "form-control")}
                                placeholder="e.g. 101"
                                value={row.formatId}
                                onChange={(e) => handleManualRowChange(index, "formatId", e.target.value)}
                              />
                              <FieldError field="formatId" index={index} errors={errors} />
                            </div>
                            <div className="col-12" data-tour={index === 0 ? "codestring" : undefined}>
                              <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                                <label className="form-label upload-label mb-0" htmlFor={fieldId("codeString", index)}>CodeString *</label>
                                <div className="d-flex align-items-center gap-3">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-link p-0"
                                    onClick={() => setBreakdownRow(index)}
                                    disabled={!canBreakDown(row)}
                                    title={canBreakDown(row) ? "See each code item and change one" : "Enter the Format Number and paste the CodeString first"}
                                  >
                                    Break down
                                  </button>
                                  <span className="text-muted small">{row.codeString.length}/{MAX_CODESTRING_LENGTH}</span>
                                </div>
                              </div>
                              <AutoGrowTextarea
                                {...fieldProps("codeString", index, errors, "form-control upload-textarea")}
                                placeholder="Paste the CodeString here"
                                maxLength={MAX_CODESTRING_LENGTH}
                                value={row.codeString}
                                onChange={(e) => handleManualRowChange(index, "codeString", e.target.value)}
                              />
                              <FieldError field="codeString" index={index} errors={errors} />
                            </div>
                          </>
                        )}
                        {rowComparesWithOrder(row, environments) && (
                          <div className="col-md-6 col-lg-4">
                            <label className="form-label upload-label" htmlFor={fieldId("order", index)}>Agile/Oracle ESOI or Work Order *</label>
                            <input
                              type="text"
                              {...fieldProps("order", index, errors, "form-control")}
                              placeholder="e.g. COST2933MCS1 or P9034160"
                              maxLength={MAX_ORDER_LENGTH}
                              value={row.order}
                              onChange={(e) => handleManualRowChange(index, "order", e.target.value)}
                            />
                            <FieldError field="order" index={index} errors={errors} />
                            <EnvironmentHint text="The order the simulation is compared with; the other one is found in Oracle. The ESOI above only names the simulation." />
                          </div>
                        )}
                        {rowUsesOracle(row, environments) && (
                          <div className="col-md-3 col-lg-2">
                            <label className="form-label upload-label" htmlFor={fieldId("plant", index)}>Plant *</label>
                            <select
                              {...fieldProps("plant", index, errors, "form-select")}
                              value={row.plant}
                              onChange={(e) => handleManualRowChange(index, "plant", e.target.value)}
                            >
                              <option value="">Choose...</option>
                              {ORACLE_PLANTS.map((plant) => (
                                <option key={plant} value={plant}>{plant}</option>
                              ))}
                            </select>
                            <FieldError field="plant" index={index} errors={errors} />
                          </div>
                        )}
                        {rowUsesAgile(row, environments) && (
                          <div className="col-md-6 col-lg-4">
                            <label className="form-label upload-label" htmlFor={fieldId("agileRevision", index)}>Agile version</label>
                            <AgileRevisionSelect
                              id={fieldId("agileRevision", index)}
                              order={simulates ? row.order : row.configurationId}
                              value={row.agileRevision}
                              onChange={(value) => handleManualRowChange(index, "agileRevision", value)}
                              signedIn={agile.session.signedIn}
                              onNeedSignIn={requestAgileSignIn}
                            />
                            <EnvironmentHint text="Agile keeps many versions of an order. Load them to compare a specific one; Default reads the one Agile opens." />
                          </div>
                        )}
                        {rowUsesAgile(row, environments) && rowUsesOracle(row, environments) && (
                          <div className="col-md-3 col-lg-2">
                            <label className="form-label upload-label" htmlFor={fieldId("includeEcoDetails", index)}>ECO Details</label>
                            <select
                              id={fieldId("includeEcoDetails", index)}
                              className="form-select"
                              value={row.includeEcoDetails}
                              onChange={(e) => handleManualRowChange(index, "includeEcoDetails", e.target.value)}
                            >
                              {ECO_DETAILS_OPTIONS.map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                            <EnvironmentHint text="Adds the Agile ECO of items whose revision differs. Slower." />
                          </div>
                        )}
                        {row.environmentB === PREVIOUS_RUN && (
                          <div className="col-md-6">
                            <label className="form-label upload-label" htmlFor={fieldId("previousRunId", index)}>Previous run *</label>
                            <PreviousRunSelect
                              id={fieldId("previousRunId", index)}
                              invalid={!!errors?.previousRunId}
                              environment={row.environmentA}
                              environments={environments}
                              value={row.previousRunId}
                              onChange={(value) => handleManualRowChange(index, "previousRunId", value)}
                            />
                            <FieldError field="previousRunId" index={index} errors={errors} />
                          </div>
                        )}
                        {isSectionRun(row) && needsCodestring && (
                          <div className="col-md-4 col-lg-3">
                            <label className="form-label upload-label" htmlFor={fieldId("sectionToProcess", index)}>Section To Process *</label>
                            <input
                              type="text"
                              {...fieldProps("sectionToProcess", index, errors, "form-control")}
                              placeholder="Section name"
                              maxLength={MAX_SECTION_LENGTH}
                              value={row.sectionToProcess}
                              onChange={(e) => handleManualRowChange(index, "sectionToProcess", e.target.value)}
                            />
                            <FieldError field="sectionToProcess" index={index} errors={errors} />
                            <EnvironmentHint
                              text={`The simulator runs only this section and returns only its data. Runs as ${effectiveConfigurationId(row) || `<ESOI>${SECTION_ONLY_SUFFIX}`}. On-premise only.`}
                            />
                          </div>
                        )}
                      </div>

                      {needsCodestring && (
                        <div className="upload-row-card__footer">
                          <div className="form-check form-switch">
                            <input
                              id={fieldId("postConfigQueue", index)}
                              className="form-check-input"
                              type="checkbox"
                              checked={row.enablePostConfigQueue}
                              onChange={(e) => handleManualRowChange(index, "enablePostConfigQueue", e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor={fieldId("postConfigQueue", index)}>
                              Post Config Queue <span className="text-muted">(saves the simulator trace)</span>
                            </label>
                          </div>
                          <div className="form-check form-switch">
                            <input
                              id={fieldId("sectionOnly", index)}
                              className="form-check-input"
                              type="checkbox"
                              checked={isSectionRun(row)}
                              disabled={isSectionOnly(row.configurationId)}
                              title={isSectionOnly(row.configurationId) ? `The ESOI already ends in ${SECTION_ONLY_SUFFIX}` : undefined}
                              onChange={(e) => handleManualRowChange(index, "sectionOnly", e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor={fieldId("sectionOnly", index)}>Only one section</label>
                          </div>
                        </div>
                      )}
                    </fieldset>
                  );
                })}
              </div>
              <button type="button" className="btn btn-sm btn-outline-primary mt-3" onClick={addManualRow}>
                + Add row
              </button>
            </>
          )}
        </div>

        {/* Stays at the bottom of the screen while the form is taller, so the result is seen where the button is */}
        <div className="upload-footer">
          {message && (
            <div
              className={`alert alert-${message.tone} upload-alert upload-footer__message d-flex justify-content-between align-items-start gap-2`}
              role={message.tone === "danger" ? "alert" : "status"}
            >
              <div>
                <div style={{ whiteSpace: "pre-wrap" }}>{message.text}</div>
                {message.rejectedRows?.length > 0 && (
                  <div className="mt-2 text-warning-emphasis">
                    {message.rejectedRows.length} row(s) were skipped:
                    <ul className="mb-0">
                      {message.rejectedRows.map((row) => (
                        <li key={row.rowNumber}>
                          Row {row.rowNumber}{row.configurationId ? ` (${row.configurationId})` : ""}: {row.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <button type="button" className="btn-close" aria-label="Close message" onClick={() => setMessage(null)}></button>
            </div>
          )}
          <div className="upload-footer__bar">
            <div className="upload-footer__info">
              <span>{file ? "From the Excel file" : `${filledRows || manualRows.length} row${(filledRows || manualRows.length) === 1 ? "" : "s"}`}</span>
              {showAgileStatus && (
                <AgileSessionStatus
                  session={agile.session}
                  onSignIn={() => requestAgileSignIn(null)}
                  onSignOut={handleAgileChangeCredentials}
                />
              )}
            </div>
            <button type="submit" className="btn btn-primary px-4" disabled={loading} data-tour="create">
              {loading ? "Creating..." : "Create test set"}
            </button>
          </div>
        </div>
      </form>

      <CodeStringBreakdown
        show={!!breakdown}
        formatNumber={breakdown ? String(breakdown.formatId) : ""}
        codeString={breakdown ? breakdown.codeString.trim() : ""}
        onHide={() => setBreakdownRow(null)}
        onApply={(codeString) => {
          handleManualRowChange(breakdownRow, "codeString", codeString);
          setBreakdownRow(null);
        }}
      />

      <AgileSignInModal
        show={showAgileSignIn}
        onHide={() => {
          setShowAgileSignIn(false);
          setAfterAgileSignIn(null);
        }}
        onSignedIn={handleAgileSignedIn}
        signIn={agile.signIn}
      />
    </div>
  );
}
